import React from 'react';
import type { StaffMember } from '../../types';
import { Icons } from '../ui/Icons';
import Modal from '../ui/Modal';

interface ReplacementSelectionModalProps {
    staffList: StaffMember[];
    onClose: () => void;
    onSelectStaff: (staff: StaffMember) => void;
}

const getInitials = (name: string) => {
    const names = name.split(' ');
    if (names.length > 1 && names[names.length - 1]) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const ReplacementSelectionModal: React.FC<ReplacementSelectionModalProps> = ({ staffList, onClose, onSelectStaff }) => {
    return (
        <Modal title="Select a Replacement" onClose={onClose}>
            <div className="p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Choose a colleague to cover your shift. Their availability will be checked before you can submit the request.</p>
                <div className="max-h-96 overflow-y-auto -mx-4">
                    <ul className="divide-y divide-gray-200 dark:divide-slate-700">
                        {staffList.length > 0 ? staffList.map(staff => (
                            <li key={staff.id}>
                                <button
                                    onClick={() => onSelectStaff(staff)}
                                    className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                                >
                                    <div
                                        className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-sm"
                                        style={{ backgroundColor: staff.color }}
                                    >
                                        {getInitials(staff.name)}
                                    </div>
                                    <div className="flex-grow">
                                        <p className="font-semibold text-gray-800 dark:text-gray-200">{staff.name}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{staff.role}</p>
                                    </div>
                                    <Icons.ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                </button>
                            </li>
                        )) : (
                            <li className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                No other staff members are available to select.
                            </li>
                        )}
                    </ul>
                </div>
            </div>
            <div className="bg-gray-50 dark:bg-slate-900/50 px-4 py-3 sm:px-6 flex justify-end border-t dark:border-slate-700">
                <button
                    type="button"
                    onClick={onClose}
                    className="w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:text-sm"
                >
                    Cancel
                </button>
            </div>
        </Modal>
    );
};

export default ReplacementSelectionModal;
